import { useEffect, useState } from "react";

import { useLatestRef } from "./useLatestRef";

interface ScrollAndPulseOptions {
  scrollDelayMs?: number;
  pulseDelayMs?: number;
  expireMs?: number;
  pulseDurationMs?: number;
  /** The DOM element to scroll to is `row-${scrollTargetId}`. Defaults to
  * `rowKey`; pass it only when the scroll target differs from the pulse key
  * (e.g. GeologyProduction keys the glow by `mvtId_truckId` but the row id is
  * just the truckId).
  */
  scrollTargetId?: string | null;
}


/** Once `rowKey` is set and `active`, it scrolls the matching row element into
* view (only if off-screen), then — after the scroll has settled — flags that
* row for a one-shot attention pulse (returned as `pulseRowKey`), and finally
* calls `onExpire` so the caller can release the key (stopping a later refetch
* from re-selecting the row). Extracted so the timing/DOM logic is testable
* without rendering the whole page.
*/
export function useScrollAndPulseRow(
  rowKey: string | null,
  active: boolean,
  onExpire: () => void,
  options: ScrollAndPulseOptions = {}
): { pulseRowKey: string | null } {
  const {
    scrollDelayMs = 300,
    pulseDelayMs = 450,
    expireMs = 4000,
    pulseDurationMs = 2600,
    scrollTargetId,
  } = options;

  const [pulseRowKey, setPulseRowKey] = useState<string | null>(null);
  const onExpireRef = useLatestRef(onExpire);

  useEffect(() => {
    if (!rowKey || !active) return;

    let pulseTimer: ReturnType<typeof setTimeout>;

    const targetId = scrollTargetId ?? rowKey;

    const scrollTimer = setTimeout(() => {
      const rowElement = document.getElementById(`row-${targetId}`);
      if (rowElement) {
        const rect = rowElement.getBoundingClientRect();
        const isVisible = rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
        if (!isVisible) rowElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      
      pulseTimer = setTimeout(() => setPulseRowKey(rowKey), pulseDelayMs); // Note: pulse only once the smooth-scroll has settled
    }, scrollDelayMs);

    const expireTimer = setTimeout(() => onExpireRef.current(), expireMs);

    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(pulseTimer);
      clearTimeout(expireTimer);
    };
  }, [rowKey, active, scrollDelayMs, pulseDelayMs, expireMs, scrollTargetId, onExpireRef]);

  useEffect(() => {
    if (!pulseRowKey) return;
    const timer = setTimeout(() => setPulseRowKey(null), pulseDurationMs);
    return () => clearTimeout(timer);
  }, [pulseRowKey, pulseDurationMs]);

  return { pulseRowKey };
}
